import React from "react";
import { useEffect, useState } from "react";
import { BuilderComponent, builder } from "@builder.io/react";
import { AppDispatch, RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import Error404Page from "../../pages/errors/404";
import "@builder.io/widgets";
import {
  BranchPageInfo,
  OverallBranches,
  StateBranches,
  SeoData,
} from "../../models/builderIO/types";
import PageLoading from "../../components/shared/pageLoading";
import { fetchWarehouseDetails } from "../../store/slices/warehouseSlice";
import SeoCommon from "@/app/utils/seo/seoCommon";
import SeoControl from "@/app/utils/seo/seoControl";
import expiringLocalStorage from "../../services/expiringLocalStorage";
import { useNavigate } from "react-router-dom";
import { useDestinationWarehouseRedirect } from "@/app/hooks/useDestinationWarehouseRedirect";
import dataService from "@/app/services/dataService";

interface BuilderIOSymbolProps {
  data?: Record<string, any>;
  currentPath: string;
}

const BuilderIOSymbol: React.FC<BuilderIOSymbolProps> = ({
  data,
  currentPath,
}) => {
  const navigate = useNavigate();

  const [notFound, setNotFound] = useState(false);
  const [content, setContent] = useState(null);
  const [branchPageInfo, setBranchPageInfo] = useState<BranchPageInfo | null>(
    null
  );
  const [stateBranches, setStateBranches] = useState<StateBranches | null>(
    null
  );
  const [overallBranches, setOverallBranches] =
    useState<OverallBranches | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const isAuthenticatedFromStore = useSelector(
    (state: RootState) => state.userState.isAuthenticated
  );

  // Redirect to destination warehouse if specified in destinationWarehouseCode query parameter
  useDestinationWarehouseRedirect(isAuthenticatedFromStore);

  const userId = useSelector(
    (state: RootState) => state.userState.user?.id ?? null
  );
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    isAuthenticatedFromStore
  );

  const [isLoading, setIsLoading] = useState(false);

  const [seoCommon, setSeoCommon] = useState<SeoData | null>(null);
  const [statePageImage, setStatePageImage] = useState<string>("");
  const [fullState, setFullState] = useState();
  const customerNumber = useSelector(
    (state: RootState) => state.userState.user?.customerNumber ?? null
  );
  const customerName = useSelector(
    (state: RootState) => state.userState.user?.customerName ?? null
  );

  // Parse URL path segments
  const pathSegments = currentPath.split("/").filter(Boolean);
  const hasLansingBranches = pathSegments.includes("lansing-branches");
  const lansingSectionIndex = pathSegments.indexOf("lansing-branches");

  // Check if this is a state-level page, branch-specific page, or overall branches page
  const isOverallPage =
    hasLansingBranches && pathSegments.length === lansingSectionIndex + 1;
  const isStatePage =
    hasLansingBranches && pathSegments.length === lansingSectionIndex + 2;
  const isBranchPage =
    hasLansingBranches && pathSegments.length === lansingSectionIndex + 3;

  const symbol = "SYMBOL";
  const stateChangedHandler = (state: any) => {
    setIsAuthenticated(state.isAuthenticated);
    console.log(`Builder.IO Symbol: ${symbol}`);
    console.log(state);
    if (state) {
      setFullState(state);
    }
    if (state.seoCommon?.data) {
      setSeoCommon(state.seoCommon.data as SeoData);
    }
    if (state.statePageDetails) {
      const stateNameFull = state.statePageDetails.stateName.toLowerCase();
      const imageUrl = state.stateImage?.data?.[stateNameFull] ?? "";
      setStatePageImage(imageUrl);
    }
  };

  const selectedWarehouse = useSelector(
    (state: RootState) => state.warehouseState?.selectedWarehouse?.warehouseCode
  );

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      const clientUtcOffsetInMinutes = new Date().getTimezoneOffset();

      const stateFullName = isStatePage
        ? pathSegments[pathSegments.length - 1]
        : null;
      const warehouseDisplayName = isBranchPage
        ? pathSegments[pathSegments.length - 1]
        : null;
      const stateNameForBranch = isBranchPage
        ? pathSegments[pathSegments.length - 2]
        : null;

      if (warehouseDisplayName && stateNameForBranch) {
        const branchDetails = await dataService.getBranchDetails(
          warehouseDisplayName
            .replace("-bu", "")
            .replace("-medenica", "")
            .replace("-main", "")
            .replace(/-test.*$/, ""), //fix for scott testing
          stateNameForBranch
            .replace("-bu", "")
            .replace("-medenica", "")
            .replace("-main", "")
            .replace(/-test.*$/, ""), //fix for scott testing
          isAuthenticated,
          clientUtcOffsetInMinutes,
          Number(userId ?? 0)
        );

        setBranchPageInfo(branchDetails.data);
      }

      if (stateFullName) {
        const stateBranches = await dataService.getStateBranches(
          stateFullName
            .replace(/-test.*$/, "")
            .replace("-bu", "")
            .replace("-medenica", "")
            .replace("-main", ""),
          clientUtcOffsetInMinutes
        );
        setStateBranches(stateBranches.data);
      }

      if (isOverallPage) {
        const overallBranches = await dataService.getOverallBranches();
        setOverallBranches(overallBranches.data);
      }

      const content = await builder
        .get("page", {
          url: currentPath
            .replace("/" + process.env.REACT_APP_SUBDIRECTORY, "")
            .replace("/" + process.env.REACT_APP_STOREFRONT_SUBDIRECTORY, ""),
        })
        .promise();

      setContent(content);
      setNotFound(!content);

      if (content) {
        setPixelProperties({ alt: "pixel tag from builder" });
      }

      setIsLoading(false);
    }

    fetchData();
  }, [isAuthenticated, currentPath, userId]);

  const reactWebUrl = isAuthenticated
    ? process.env.REACT_APP_WEB_URL +
      "/" +
      process.env.REACT_APP_STOREFRONT_SUBDIRECTORY
    : process.env.REACT_APP_WEB_URL + "/" + process.env.REACT_APP_SUBDIRECTORY;

  if (notFound) {
    return <Error404Page />;
  }

  if (isLoading) {
    return <PageLoading />;
  }

  const branchName = branchPageInfo?.displayName || "";
  const branchNameEncoded = encodeURIComponent(branchName);
  const customerData = {
    customerNumber: customerNumber,
    customerName: customerName,
  };

  function setPixelProperties(props: Record<string, string>) {
    setTimeout(() => {
      const pixelImages = document.querySelectorAll(
        'img[src*="cdn.builder.io/api/v1/pixel"]'
      );

      pixelImages.forEach((img) => {
        for (const [key, value] of Object.entries(props)) {
          img.setAttribute(key, value);
        }
      });
    }, 0);
  }

  function updateSelectedWarehouse(
    newWarehouseCode: string,
    redirectUrl: string | null | undefined = null
  ) {
    expiringLocalStorage.setItem(
      expiringLocalStorage.localStorageKeys.geolocatedWarehouse,
      newWarehouseCode
    );
    dispatch(fetchWarehouseDetails(newWarehouseCode));
    if (redirectUrl !== null && redirectUrl !== undefined)
      navigate(redirectUrl);
  }

  // return the page when found
  return (
    <div
      className={
        isAuthenticated
          ? "builder-page-width builder-page-width-logged"
          : "builder-page-width"
      }
    >
      <SeoControl
        noindex={
          seoCommon === null ||
          seoCommon === undefined ||
          seoCommon.noIndex === true
        }
        nofollow={
          seoCommon === null ||
          seoCommon === undefined ||
          seoCommon.noFollow === true
        }
      />
      {seoCommon && Object.keys(seoCommon).length > 0 && (
        <SeoCommon
          seoCommon={seoCommon}
          content={content}
          templateData={fullState}
          isOverallPage={isOverallPage}
          isStatePage={isStatePage}
          isBranchPage={isBranchPage}
        ></SeoCommon>
      )}
      {/* Render the Builder page */}
      <BuilderComponent
        model="symbol"
        content={content!}
        onStateChange={stateChangedHandler}
        data={{
          ...data,
          isAuthenticated: isAuthenticated,
          ...(branchPageInfo && { branchPageDetails: branchPageInfo }),
          ...(branchPageInfo && { categories: branchPageInfo.categories }),
          ...(stateBranches && { statePageDetails: stateBranches }),
          ...(overallBranches && { overallPageDetails: overallBranches }),
          apiUrl: process.env.REACT_APP_STOREFRONT_API_URL?.replace(
            "https://",
            ""
          ),
          apiBaseUrl: process.env.REACT_APP_BASE_API_URL?.replace(
            "https://",
            ""
          ),
          webUrl: process.env.REACT_APP_WEB_URL?.replace("https://", ""),
          reactWebUrl: reactWebUrl,
          baseWebUrl: process.env.REACT_APP_WEB_URL,
          selectedWarehouse: selectedWarehouse,
          ...(branchPageInfo && { branchNameEncoded: branchNameEncoded }),
          ...(customerData && { customer: customerData }),
        }}
      />
    </div>
  );
};

export default BuilderIOSymbol;
